import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "next/navigation";
import useSWR from "swr";
import { fetcher } from "@/src/utils/fetcher";
import { create, all } from "mathjs";
import { useDebounce } from "@/src/utils/debounce";
import {
  CalculateFieldWidgetProps,
  JSONValue,
} from "@/src/types/components/ScenarioForm";

//types
import { formValues } from "@/src/types/api";

const math = create(all);

const extractQuestionIds = (calculationString: string): string[] => {
  const regex = /\d+/g;
  return calculationString.match(regex) || [];
};

const extractBackgroundDataIds = (calculationString: string) => {
  const regex = /backgroundData\((\d+)\)/g;
  const matches = [];
  let match;
  while ((match = regex.exec(calculationString)) !== null) {
    matches.push(match[1]);
  }
  return matches;
};

const findValueRecursively = (
  data: formValues,
  id: string
): JSONValue | null => {
  if (typeof data !== "object" || data === null) {
    return null;
  }

  for (const key in data) {
    if (data.hasOwnProperty(key)) {
      if (key === id && typeof data[key] !== "object") {
        return data[key] as JSONValue; // Explicitly cast to JSONValue
      }
      const value = findValueRecursively(
        data[key] as unknown as formValues,
        id
      );
      if (value !== null) {
        return value;
      }
    }
  }

  return null;
};

const getValue = (id: string, formData: formValues): JSONValue => {
  const valueFromFormData = findValueRecursively(formData, id);
  if (valueFromFormData !== null) {
    return valueFromFormData;
  }
  console.warn(`Value for ID ${id} not found. Defaulting to 0.`);
  return 0; // Default value if ID is not found
};

const performSimpleCalculation = (
  calculationString: string,
  formData: formValues
): number => {
  const questionIds = extractQuestionIds(calculationString);
  let preparedString = calculationString;

  questionIds.forEach((id) => {
    const value = getValue(id, formData) ?? 0; // Fallback to 0 if value is undefined
    preparedString = preparedString.replace(
      new RegExp(`\\b${id}\\b`, "g"),
      value.toString()
    );
  });

  try {
    const expr = math.parse(preparedString);
    const result = expr.evaluate();

    // Check for Infinity or NaN and return 0 if detected
    if (!isFinite(result) || isNaN(result)) {
      console.warn("Calculation resulted in Infinity or NaN. Returning 0.");
      return 0;
    }

    return result;
  } catch (error) {
    console.error("Error evaluating expression:", error);
    return 0; // Return a default value in case of error
  }
};

function useBackgroundQuestion(
  scenarioId: string | string[],
  backgroundQuestionId: string[]
) {
  const searchParams = new URLSearchParams();
  searchParams.append("questionIds", backgroundQuestionId.join(","));

  const { data, error } = useSWR(
    scenarioId
      ? `/api/scenarios/${scenarioId}?${searchParams.toString()}`
      : null,
    fetcher
  );

  return { data, error };
}

const performComplexCalculation = (
  calculationString: string,
  formData: formValues,
  backgroundData: formValues,
  backgroundKey: string
) => {
  const questionIds = extractQuestionIds(calculationString);
  const values: { [key: string]: JSONValue } = {};

  questionIds.forEach((id) => {
    values[id] = getValue(id, formData);
  });

  const conditionalRegex = /(\d+)=false;backgroundData\((\d+)\):(\d+)/g;
  let preparedString = calculationString.replace(
    conditionalRegex,
    (match, conditionId, backgroundId, alternativeId) => {
      const conditionValue = values[conditionId];
      const backgroundValue = backgroundData[backgroundKey];
      const alternativeValue = values[alternativeId];

      // Ensure conditionValue is explicitly checked
      return `${conditionValue === false ? backgroundValue : alternativeValue}`;
    }
  );

  questionIds.forEach((id) => {
    const value = values[id];
    preparedString = preparedString.replace(
      new RegExp(`\\b${id}\\b`, "g"),
      value.toString()
    );
  });

  try {
    const expr = math.parse(preparedString);
    const result = expr.evaluate();

    // Check for Infinity or NaN and return 0 if detected
    if (!isFinite(result) || isNaN(result)) {
      console.warn("Calculation resulted in Infinity or NaN. Returning 0.");
      return 0;
    }

    return result;
  } catch (error) {
    console.error("Error evaluating expression:", error);
    return 0; // Return a default value in case of error
  }
};

// Note: We're now using the useDebounce hook from our performance utility

export default function CalculateFieldWidget({
  formContext,
  schema,
  onChange,
}: CalculateFieldWidgetProps): React.JSX.Element {
  const [calculatedValue, setCalculatedValue] = useState(0);
  const [backgroundDataKey, setBackgroundDataKey] = useState(null);
  const formData = formContext.formData;
  const { id } = useParams();

  // Use useMemo to prevent recreating this array on every render
  const backgroundQuestionIds = useMemo(() =>
    schema.backgroundData
      ? extractBackgroundDataIds(schema.computedCalculation)
      : []
  , [schema.backgroundData, schema.computedCalculation]);
 
  const { data, error } = useBackgroundQuestion(id || '', backgroundQuestionIds);

  // Create a stable calculation function that can be debounced
  const calculateValue = useCallback(() => {
    const calculationString = schema.computedCalculation;
    let result;

    if (schema.backgroundData && backgroundDataKey) {
      result = performComplexCalculation(
        calculationString,
        formData,
        schema.backgroundData,
        backgroundDataKey
      );
    } else {
      result = performSimpleCalculation(calculationString, formData);
    }

    // Prevent unnecessary state updates
    if (result !== calculatedValue) {
      setCalculatedValue(result);
      onChange(result);
    }
  }, [
    formData,
    schema.computedCalculation,
    schema.backgroundData,
    backgroundDataKey,
    calculatedValue,
    onChange
  ]);
 
  // Create debounced version of calculation function
  const [debouncedCalculate] = useDebounce(calculateValue, 300);

  useEffect(() => {
    if (schema.backgroundData) {
      if (data && backgroundQuestionIds.length > 0) {
        //This now assumes that there in only one background question
        //If there are multiple, we need to change this
        const backgroundDataValue = data[backgroundQuestionIds[0].toString()];
        setBackgroundDataKey(backgroundDataValue);
      }
      if (error) {
        console.error("Error fetching background data:", error);
      }
    }
  }, [data, error, backgroundQuestionIds, schema.backgroundData]);

  // Trigger calculation whenever dependencies change
  useEffect(() => {
    debouncedCalculate();
   
    // No need for explicit cleanup as it's handled in the useDebounce hook
  }, [
    formData,
    schema.computedCalculation,
    schema.backgroundData,
    backgroundDataKey,
    debouncedCalculate
  ]);
  return <div>The calculated value is {calculatedValue}</div>;
}